import { CHANNEL_ID, LOG_ID } from '@@types/common';
import Log, { AllSearchLog } from '@@types/Log';
import { Modifier } from '@popperjs/core/lib/types';

import { MenuPopperLog } from '~/components/Poppers/LogMenuPopper/LogMenuPopper';
import { ChannelType, LOG_MENU, PopperType } from '~/constants';
import { Place } from '~/contexts/Channel/ChannelContext';
import Actions from '~/stores/Actions';
import { uiState } from '~/stores/UiState';
import {
  canCreateThreadLog,
  getPreviewLog,
  isControllable,
  isForwardLog,
  isTextLog,
  isWebHookLog,
} from '~/utils/channelLogsUtil';
import domUtil from '~/utils/domUtil';
import { isNil } from 'ramda';

class PopperActions {
  constructor(private actions: Actions) {}

  public handleMouseEnterLog = (
    logId: LOG_ID,
    wrapper$: HTMLDivElement,
    {
      keyPrefix = '',
      log,
      disabled,
      narrowPopper = false,
      startLog,
      place,
      showMenus,
      threadCoachMark,
      inThread,
      boundary,
    }: {
      keyPrefix?: string;
      log?: Log | AllSearchLog;
      disabled?: boolean;
      narrowPopper?: boolean;
      showMenus?: LOG_MENU[];
      startLog?: boolean;
      place?: Place;
      threadCoachMark?: boolean;
      inThread?: boolean;
      boundary?: HTMLElement;
    } = {}
  ) => {
    if (!this.canShowMenu(disabled, log) || this.isOpened(logId, log)) {
      return;
    }
    const showMenuTypeList = this.getLogMenuList(showMenus, log);
    const key = this.makeKey(logId, keyPrefix);
    uiState.setHoverLog(key);
    const defaultOffset = narrowPopper
      ? ({ popper }) => [popper.width / 2 - 4, 3 - popper.height / 2 - 2]
      : ({ popper }) => [popper.width / 2 + 14, 3 - popper.height / 2];
    uiState.closePopper(PopperType.LogMenu);
    window.requestAnimationFrame(() => {
      if (uiState.openSelectEmoticon && uiState.openSelectEmoticon !== logId) {
        uiState.closePopper(PopperType.StickerEmoticon);
      }

      uiState.openPopper({
        name: PopperType.LogMenu,
        place,
        args: {
          withRef: true,
          referenceNode: wrapper$,
          options: {
            placement: 'top-end',
            modifiers: [
              { name: 'flip', options: { fallbackPlacements: ['bottom-end'], boundary } },
              { name: 'offset', options: { offset: defaultOffset } },
              { name: 'preventOverflow', options: { padding: 19 } },
            ],
          },
          logId,
          log,
          key,
          startLog,
          place,
          showMenuTypeList,
          threadCoachMark,
          inThread,
        },
      });
    });
  };

  // menu hover까지 시간을 약간 대기
  public handleMouseLeaveLog = domUtil.singletonRequestAnimationFrame(
    (
      logId: LOG_ID,
      options: {
        keyPrefix?: string;
        log?: Log | AllSearchLog;
      }
    ) => {
      const key = this.makeKey(logId, options.keyPrefix);
      if (uiState.openSelectEmoticon) {
        return;
      }
      this.closeMenuPopper(logId, options);
      uiState.clearHoverLog(key);
      if (uiState.popperMap[PopperType.StickerEmoticon]?.args.trigger === 'logMenuPopper') {
        uiState.closePopper(PopperType.StickerEmoticon);
      }
    }
  );

  public closeMenuPopper = (
    logId: LOG_ID,
    {
      keyPrefix,
      log,
    }: {
      keyPrefix?: string;
      log?: Log | AllSearchLog;
    }
  ) => {
    const key = this.makeKey(logId, keyPrefix);
    if (this.isOpened(logId, log) && uiState.hoverTriggerLog !== logId && !uiState.hoverLogMenu[key]) {
      uiState.closePopper(PopperType.LogMenu);
    }
  };

  private makeKey = (logId: LOG_ID, keyPrefix = '') => {
    return `${keyPrefix}${logId}`;
  };

  private canShowMenu = (disabled: boolean, log: Log | AllSearchLog) => {
    return !disabled && this.actions.permissionActions.showLogMenuPopper && !isNil(log) && isControllable(log);
  };

  private getLogMenuList = (showMenus: LOG_MENU[], log: Log | AllSearchLog) => {
    const isReadonly = this.actions.permissionActions.isReadonlyChannelId(log.channelId);
    const logDetail = getPreviewLog(log).getOrElse({}) as MenuPopperLog;
    const hasTextLogValue = isTextLog(logDetail);
    const canCreateThreadLogValue = canCreateThreadLog(log);
    const canEditOrDeleteLogValue = this.actions.permissionActions.canEditOrDeleteLog(log as Log);
    const logMenu: Record<LOG_MENU, () => boolean> = {
      [LOG_MENU.THREAD]: () => !isReadonly && canCreateThreadLogValue,
      [LOG_MENU.REPLY]: () => !isReadonly,
      [LOG_MENU.FORWARD]: () => true,
      [LOG_MENU.NOTICE]: () => !isReadonly && (hasTextLogValue || isWebHookLog(log)),
      [LOG_MENU.EDIT]: () => canEditOrDeleteLogValue && !isForwardLog(log) && hasTextLogValue,
      [LOG_MENU.DELETE]: () => canEditOrDeleteLogValue,
      [LOG_MENU.OK]: () => !isReadonly,
      [LOG_MENU.HEARTS]: () => !isReadonly,
      [LOG_MENU.THUMB_UP]: () => !isReadonly,
      [LOG_MENU.HEAVY_CHECK_MARK]: () => !isReadonly,
      [LOG_MENU.REACTION]: () => !isReadonly,
    };
    return showMenus.filter((menu) => logMenu[menu]());
  };

  private isOpened = (logId: LOG_ID, log: Log | AllSearchLog) => {
    const openedLogId = uiState.popperMap[PopperType.LogMenu]?.args?.logId;
    const openedLog = uiState.popperMap[PopperType.LogMenu]?.args?.log;
    return (logId && openedLogId === logId) || (log && openedLog === log);
  };

  public openEmoticonPopper = ({
    place,
    channelId,
    logId,
    type,
    referenceNode,
    modifiers = [],
    selectEmoticon = false,
    onClose,
    trigger,
  }: {
    place: Place;
    channelId: CHANNEL_ID;
    logId?: LOG_ID;
    type: ChannelType;
    referenceNode: HTMLElement;
    modifiers?: Array<Partial<Modifier<any, any>>>;
    selectEmoticon?: boolean;
    onClose?: (name: string) => void;
    trigger: 'logMenuPopper' | 'editor' | 'selectEmoticon';
  }) => {
    uiState.openPopper({
      name: PopperType.StickerEmoticon,
      args: {
        trigger,
        channelId,
        logId,
        type,
        referenceNode,
        selectEmoticon,
        options: {
          placement: 'top-start',
          modifiers: [{ name: 'flip', options: { fallbackPlacements: ['top-start', 'bottom-start'] } }, ...modifiers],
        },
      },
      place,
      onClose,
    });
  };
}

export default PopperActions;
